import { useCollaborations } from '@/hooks/useCollaborations'
import { useState, useEffect } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { toast } from '@/components/ui/sonner'
import {
  BanknoteIcon,
  Check,
  Link,
  User,
  CheckCircle,
  Clock,
  ExternalLink,
  FileText,
  ChevronRight,
  AlertTriangle,
} from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { calculateEarnings } from '@/utils'
import athleteApi from '@/lib/api'
import { Card } from '@/components/ui/card'

interface PendingInvoice {
  id: string
  collaboration_id: string
  invoice_number: string
  status: 'sent_to_athlete' | 'sent_to_brand' | 'paid' | 'draft' | 'void'
  total_cents: number
  currency: string
  sent_to_athlete_at: string | null
  sent_to_brand_at: string | null
}

type StripeStatus = {
  connected: boolean
  status: 'not_connected' | 'pending' | 'restricted' | 'complete'
  details_submitted?: boolean
  payouts_enabled?: boolean
  charges_enabled?: boolean
  created_at?: string
}

const Payments = () => {
  const { collaborations, refetch: refreshCollaborations } = useCollaborations()
  const { athlete } = useAuth()
  const [submitting, setSubmitting] = useState(false)
  const [activeTab, setActiveTab] = useState('overview')
  const [stripeStatus, setStripeStatus] = useState<StripeStatus | null>(null)
  const [loadingStatus, setLoadingStatus] = useState(true)
  const [invoices, setInvoices] = useState<PendingInvoice[]>([])

  // Fetch Stripe connection status on mount
  useEffect(() => {
    const fetchStripeStatus = async () => {
      try {
        const response = await athleteApi.get('/athlete/stripe/status')
        setStripeStatus(response.data)
      } catch (error) {
        console.error('Error fetching Stripe status:', error)
        setStripeStatus({ connected: false, status: 'not_connected' })
      } finally {
        setLoadingStatus(false)
      }
    }

    fetchStripeStatus()
  }, [])

  // Load invoices visible to this athlete (review queue + sent + paid).
  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const res = await athleteApi.get('/athlete/invoices')
        setInvoices(res.data?.invoices ?? [])
      } catch (error) {
        console.error('Error fetching invoices:', error)
      }
    }
    fetchInvoices()
  }, [])

  // Calculate total earnings and pending payments
  const completedCollaborations = collaborations.filter(
    (collaboration) =>
      collaboration.paymentStatus === 'paid' || collaboration.paymentStatus === 'pending',
  )

  const { totalEarnings, pendingPayments, paidPayments } = calculateEarnings(collaborations)

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const handleFixStripeAccount = async () => {
    setSubmitting(true)
    try {
      const response = await athleteApi.post('/athlete/stripe/refresh-link')
      window.location.href = response.data.url
    } catch (error) {
      console.error('Error generating Stripe update link:', error)
      toast.error('Error', { description: 'Failed to open Stripe. Please try again.' })
      setSubmitting(false)
    }
  }

  const handleConnectBank = async () => {
    setSubmitting(true)

    try {
      const response = await athleteApi.post('/athlete/stripe/connect')
      const { url } = response.data

      // Redirect to Stripe-hosted onboarding
      window.location.href = url
    } catch (error: unknown) {
      console.error('Error connecting to Stripe:', error)

      // Check if already connected
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { status?: string } } }
        if (axiosError.response?.data?.status === 'complete') {
          toast('Already Connected', {
            description: 'Your account is already connected and enabled for payouts.',
          })
          setSubmitting(false)
          return
        }
      }

      toast.error('Connection Error', {
        description: 'Failed to connect to Stripe. Please try again.',
      })
      setSubmitting(false)
    }
  }

  const getConnectionStatusBadge = () => {
    if (loadingStatus) {
      return (
        <span className="px-2 py-1 text-xs bg-gray-500/20 text-gray-300 rounded-full">
          Loading...
        </span>
      )
    }

    if (!stripeStatus?.connected) {
      return (
        <span className="px-2 py-1 text-xs bg-red-500/20 text-red-300 rounded-full border border-red-500/30">
          Not Connected
        </span>
      )
    }

    if (stripeStatus.status === 'complete') {
      return (
        <span className="px-2 py-1 text-xs bg-green-500/20 text-green-300 rounded-full border border-green-500/30 flex items-center gap-1">
          <CheckCircle className="h-3 w-3" /> Connected
        </span>
      )
    }

    if (stripeStatus.status === 'restricted') {
      return (
        <span className="px-2 py-1 text-xs bg-red-500/20 text-red-300 rounded-full border border-red-500/30 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" /> Action Required
        </span>
      )
    }

    return (
      <span className="px-2 py-1 text-xs bg-yellow-500/20 text-yellow-300 rounded-full border border-yellow-500/30 flex items-center gap-1">
        <Clock className="h-3 w-3" /> Pending Verification
      </span>
    )
  }

  return (
    <div className="pb-4 max-w-full">
      <div className="mb-4 md:mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Earnings</h1>
          <p className="text-gray-400">Track your earnings and manage payment methods</p>
        </div>
        <button
          onClick={() => refreshCollaborations()}
          className="text-[#C0FD72] hover:text-[#d2ff9c] flex items-center justify-center w-8 h-8 rounded-full"
        ></button>
      </div>

      <Tabs defaultValue="overview" className="w-full" onValueChange={setActiveTab}>
        <TabsList className="mb-6 bg-white/10 border border-white/10">
          <TabsTrigger
            value="overview"
            className="data-[state=active]:bg-[#C0FD72] data-[state=active]:text-black"
          >
            Overview
          </TabsTrigger>
          <TabsTrigger
            value="setup"
            className="data-[state=active]:bg-[#C0FD72] data-[state=active]:text-black"
          >
            Payment Setup
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <PendingInvoicesPanel invoices={invoices} />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <Card variant="dark" className="overflow-hidden">
                <div className="p-6">
                  <h3 className="text-md font-bold text-white mb-4">Earnings Summary</h3>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-blue-500/20 p-4 rounded-lg border border-blue-500/30">
                      <h3 className="text-sm font-medium text-gray-300 mb-1">Total Earnings</h3>
                      <p className="text-2xl font-bold text-white">
                        {formatCurrency(totalEarnings)}
                      </p>
                    </div>
                    <div className="bg-green-500/20 p-4 rounded-lg border border-green-500/30">
                      <h3 className="text-sm font-medium text-gray-300 mb-1">Paid</h3>
                      <p className="text-2xl font-bold text-green-300">
                        {formatCurrency(paidPayments)}
                      </p>
                    </div>
                    <div className="bg-[#F9DA4C]/20 p-4 rounded-lg border border-[#F9DA4C]/30">
                      <h3 className="text-sm font-medium text-gray-300 mb-1">Pending</h3>
                      <p className="text-2xl font-bold text-[#F9DA4C]">
                        {formatCurrency(pendingPayments)}
                      </p>
                    </div>
                  </div>

                  {totalEarnings > 0 ? (
                    <div>
                      <h3 className="text-md font-medium mb-3 text-white">Payment History</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="bg-black/20 border-b border-white/10">
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                Campaign
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                Amount
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                Date
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-white/10">
                            {completedCollaborations.map((collaboration) => (
                              <tr key={collaboration.id} className="hover:bg-white/5">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <div className="h-8 w-8 shrink-0 mr-3">
                                      <img
                                        src={collaboration.brand.logo}
                                        alt={collaboration.brand.name}
                                        className="h-8 w-8 rounded-full"
                                      />
                                    </div>
                                    <div>
                                      <div className="text-sm font-medium text-white">
                                        {collaboration.title}
                                      </div>
                                      <div className="text-sm text-gray-400">
                                        {collaboration.brand.name}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                                  {formatCurrency(Number(collaboration.paymentAmount))}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  {collaboration.paymentStatus === 'paid' ? (
                                    <span className="px-2 py-1 text-xs bg-green-900/50 text-green-200 rounded-full border border-green-700/50">
                                      Paid
                                    </span>
                                  ) : (
                                    <span className="px-2 py-1 text-xs bg-[#F9DA4C]/20 text-[#F9DA4C] rounded-full border border-[#F9DA4C]/30">
                                      Pending
                                    </span>
                                  )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                                  {collaboration.paymentDate
                                    ? formatDate(collaboration.paymentDate)
                                    : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <p className="text-gray-400">No payment history available yet.</p>
                    </div>
                  )}
                </div>
              </Card>
            </div>

            <div>
              <Card variant="dark" className="overflow-hidden h-max sticky top-6">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xl font-bold text-white">Connected Account</h3>
                    {getConnectionStatusBadge()}
                  </div>
                  <p className="text-gray-400 text-sm mb-4">How you receive your earnings</p>

                  {stripeStatus?.connected && stripeStatus.status === 'complete' ? (
                    <div className="bg-green-500/20 p-4 rounded-lg mb-4 border border-green-500/30">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle className="h-4 w-4 text-green-400" />
                        <h3 className="text-sm font-medium text-white">Bank Account Connected</h3>
                      </div>
                      <p className="text-gray-300 text-sm">Connected via Stripe</p>
                      {stripeStatus.created_at && (
                        <p className="text-gray-400 text-xs mt-1">
                          Since {formatDate(stripeStatus.created_at)}
                        </p>
                      )}
                    </div>
                  ) : stripeStatus?.connected && stripeStatus.status === 'restricted' ? (
                    <div className="bg-red-500/20 p-4 rounded-lg border border-red-500/30 mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="h-4 w-4 text-red-400" />
                        <h3 className="text-sm font-medium text-red-300">Action Required</h3>
                      </div>
                      <p className="text-gray-300 text-sm mb-3">
                        Your Stripe account has been restricted. Stripe needs additional
                        information to activate your card payments and payouts.
                      </p>
                      <button
                        onClick={handleFixStripeAccount}
                        disabled={submitting}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                      >
                        <ExternalLink className="h-4 w-4" />
                        {submitting ? 'Opening Stripe...' : 'Resolve in Stripe'}
                      </button>
                    </div>
                  ) : stripeStatus?.connected && stripeStatus.status === 'pending' ? (
                    <div className="bg-[#F9DA4C]/20 p-4 rounded-lg border border-[#F9DA4C]/30 mb-4">
                      <div className="flex items-center gap-2 mb-1">
                        <Clock className="h-4 w-4 text-[#F9DA4C]" />
                        <h3 className="text-sm font-medium text-[#F9DA4C]">
                          Verification Pending
                        </h3>
                      </div>
                      <p className="text-gray-300 text-sm">
                        Your account is being verified. This usually takes 1-2 business days.
                      </p>
                    </div>
                  ) : (
                    <div className="bg-[#F9DA4C]/20 p-4 rounded-lg border border-[#F9DA4C]/30 mb-4">
                      <h3 className="text-sm font-medium text-[#F9DA4C] mb-1">
                        No Connected Account
                      </h3>
                      <p className="text-gray-300 text-sm">
                        Please add a payment method to receive your earnings
                      </p>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="setup" className="space-y-6">
          <Card variant="dark" className="overflow-hidden">
            <div className="p-6">
              <div>
                <h3 className="text-2xl font-bold text-white mb-2">Get Paid Fast and Direct</h3>
                <p className="text-lg text-gray-300 mb-6">
                  Connect your bank account securely using Stripe. No fees. No hassle.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <div className="bg-black/20 p-6 rounded-lg text-center border border-white/10">
                    <div className="mx-auto mb-4 w-12 h-12 bg-blue-900/50 rounded-full flex items-center justify-center border border-blue-500/30">
                      <User className="h-6 w-6 text-blue-300" />
                    </div>
                    <h3 className="font-medium mb-2 text-white">1. Join a campaign</h3>
                    <p className="text-gray-400 text-sm">
                      Accept brand deals that match your style and values
                    </p>
                  </div>

                  <div className="bg-black/20 p-6 rounded-lg text-center border border-white/10">
                    <div className="mx-auto mb-4 w-12 h-12 bg-blue-900/50 rounded-full flex items-center justify-center border border-blue-500/30">
                      <Link className="h-6 w-6 text-blue-300" />
                    </div>
                    <h3 className="font-medium mb-2 text-white">2. Connect your bank</h3>
                    <p className="text-gray-400 text-sm">Securely link your account with Stripe</p>
                  </div>

                  <div className="bg-black/20 p-6 rounded-lg text-center border border-white/10">
                    <div className="mx-auto mb-4 w-12 h-12 bg-blue-900/50 rounded-full flex items-center justify-center border border-blue-500/30">
                      <BanknoteIcon className="h-6 w-6 text-blue-300" />
                    </div>
                    <h3 className="font-medium mb-2 text-white">3. Get paid directly</h3>
                    <p className="text-gray-400 text-sm">Receive earnings via direct deposit</p>
                  </div>
                </div>

                <div className="bg-black/20 p-6 rounded-lg flex items-center border border-white/10 mb-6">
                  <div className="flex-1">
                    <div className="flex items-center mb-4">
                      <div className="rounded-full bg-[#C0FD72] p-1 mr-3">
                        <Check className="h-4 w-4 text-black" />
                      </div>
                      <p className="text-white font-medium">Secure bank connection</p>
                    </div>
                    <div className="flex items-center mb-4">
                      <div className="rounded-full bg-[#C0FD72] p-1 mr-3">
                        <Check className="h-4 w-4 text-black" />
                      </div>
                      <p className="text-white font-medium">No middleman fees</p>
                    </div>
                    <div className="flex items-center">
                      <div className="rounded-full bg-[#C0FD72] p-1 mr-3">
                        <Check className="h-4 w-4 text-black" />
                      </div>
                      <p className="text-white font-medium">Fast processing</p>
                    </div>
                  </div>
                  <div className="hidden md:block w-1/3">
                    <div className="bg-linear-to-br from-[#635BFF] to-[#00D4FF] p-4 rounded-lg text-center">
                      <p className="text-white font-bold text-lg">Powered by</p>
                      <p className="text-white font-bold text-2xl">Stripe</p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-500/20 p-4 rounded-lg border border-blue-500/30 mb-6">
                  <p className="text-sm text-blue-300">
                    <span className="font-medium">Security note:</span> Athios uses Stripe to
                    securely connect your bank account. We never store your banking credentials.
                  </p>
                </div>

                <p className="text-xs text-gray-400 mb-6">
                  All payments are logged, FMV-compliant, and reported for end-of-year tax
                  purposes.
                </p>

                {stripeStatus?.connected && stripeStatus.status === 'complete' ? (
                  <div className="flex items-center justify-between p-4 bg-green-500/20 rounded-lg border border-green-500/30">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-400" />
                      <span className="text-white font-medium">
                        Your account is connected and ready for payouts!
                      </span>
                    </div>
                  </div>
                ) : stripeStatus?.connected && stripeStatus.status === 'pending' ? (
                  <button
                    onClick={handleConnectBank}
                    disabled={submitting}
                    className="px-8 py-3 bg-[#C0FD72] text-black rounded-md hover:bg-[#d2ff9c] flex items-center gap-2"
                  >
                    {submitting ? (
                      <span className="flex items-center">
                        <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-black border-t-transparent"></span>
                        Connecting...
                      </span>
                    ) : (
                      <>
                        <ExternalLink className="h-4 w-4" />
                        Complete Account Setup
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={handleConnectBank}
                    disabled={submitting}
                    className="px-8 py-3 bg-[#C0FD72] text-black rounded-md hover:bg-[#d2ff9c] flex items-center gap-2"
                  >
                    {submitting ? (
                      <span className="flex items-center">
                        <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-black border-t-transparent"></span>
                        Connecting...
                      </span>
                    ) : (
                      <>
                        <ExternalLink className="h-4 w-4" />
                        Connect My Bank Account
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}

const PendingInvoicesPanel = ({ invoices }: { invoices: PendingInvoice[] }) => {
  const visible = invoices.filter(
    (inv) => inv.status === 'sent_to_athlete' || inv.status === 'sent_to_brand',
  )
  if (visible.length === 0) return null

  const formatCents = (cents: number, currency: string) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: (currency || 'usd').toUpperCase(),
      maximumFractionDigits: 0,
    }).format(cents / 100)

  return (
    <Card variant="dark" className="overflow-hidden">
      <div className="p-5">
        <h3 className="text-md font-bold text-white mb-3 flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#C0FD72]" />
          Invoices
        </h3>
        <div className="flex flex-col gap-2">
          {visible.map((inv) => {
            const pendingReview = inv.status === 'sent_to_athlete'
            return (
              <RouterLink
                key={inv.id}
                to={`/campaigns/${inv.collaboration_id}/invoice`}
                className="group flex items-center gap-3 rounded-xl border border-white/10 bg-white/3 hover:bg-white/6 px-4 py-3 transition-colors"
              >
                <div
                  className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                    pendingReview ? 'bg-blue-500/15' : 'bg-[#C0FD72]/15'
                  }`}
                >
                  {pendingReview ? (
                    <Clock className="w-4 h-4 text-blue-300" />
                  ) : (
                    <CheckCircle className="w-4 h-4 text-[#C0FD72]" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{inv.invoice_number}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {pendingReview ? 'Awaiting your review' : `Sent to brand · awaiting payment`}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-bold text-white font-mono">
                    {formatCents(inv.total_cents, inv.currency)}
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-500 group-hover:text-gray-300 shrink-0" />
              </RouterLink>
            )
          })}
        </div>
      </div>
    </Card>
  )
}

export default Payments
